import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './DataTable.css';
import './DataTable-pagination-styles.css';

export interface DataTableAction {
  key: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'warning' | 'success' | 'info';
  disabled?: boolean;
}

export interface DataTableColumn<T = any> {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (value: any, row: T, index: number) => React.ReactNode;
  className?: string;
  width?: number; // largura inicial em pixels
  minWidth?: number; // largura mínima em pixels
  resizable?: boolean; // permite redimensionar (padrão: true)
  checkboxColumn?: boolean; // indica que esta coluna é de checkbox e deve ter checkbox no header
  allChecked?: boolean; // estado do checkbox do header (todos selecionados)
  onCheckboxHeaderClick?: () => void; // callback quando checkbox do header é clicado
}

export interface DataTableProps<T = any> {
  columns: DataTableColumn<T>[];
  data: T[];
  keyExtractor: (row: T, index: number) => string | number;
  sortColumn?: string | null;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string, direction?: 'asc' | 'desc') => void;
  onClearSort?: () => void;
  onRowClick?: (row: T, index: number) => void;
  loading?: boolean;
  emptyMessage?: string;
  striped?: boolean;
  hover?: boolean;
  bordered?: boolean;
  responsive?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showColumnMenu?: boolean; // mostra menu para exibir/ocultar colunas (padrão: true)
  onFilter?: (columnKey: string, value: string) => void; // callback para filtros
  onClearFilters?: () => void; // callback para limpar filtros controlados em lote
  filters?: { [key: string]: string }; // filtros ativos
  // Paginação
  pagination?: boolean; // habilita paginação (padrão: false)
  clientSidePagination?: boolean; // quando true, pagina internamente o array data
  currentPage?: number; // página atual (1-based)
  pageSize?: number; // itens por página
  totalItems?: number; // total de itens (necessário para calcular total de páginas)
  onPageChange?: (page: number) => void; // callback quando página muda
  onPageSizeChange?: (pageSize: number) => void; // callback quando itens por página muda
  pageSizeOptions?: number[]; // opções de itens por página (padrão: [5, 10, 25, 50])
  initialVisibleColumns?: { [key: string]: boolean }; // visibilidade inicial das colunas
  exportFileName?: string; // nome base do arquivo CSV exportado
}

// Componente de menu de ações padronizado
export const ActionMenu: React.FC<{ actions: DataTableAction[]; triggerSize?: 'sm' | 'md' }> = ({ actions, triggerSize = 'sm' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Node;
    const clickedTrigger = !!menuRef.current && menuRef.current.contains(target);
    const clickedDropdown = !!dropdownRef.current && dropdownRef.current.contains(target);
    if (!clickedTrigger && !clickedDropdown) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, handleClickOutside]);

  // Calcular posição do dropdown
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const updateDropdownPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const dropdownWidth = 200;
    const viewportPadding = 8;
    const gap = 4;
    const minPreferredHeight = 120;
    const availableBelow = window.innerHeight - (rect.bottom + gap) - viewportPadding;
    const availableAbove = rect.top - gap - viewportPadding;
    const shouldOpenUp = availableBelow < minPreferredHeight && availableAbove > availableBelow;
    const availableSpace = shouldOpenUp ? availableAbove : availableBelow;
    const maxHeight = Math.max(120, Math.min(window.innerHeight - viewportPadding * 2, availableSpace));

    const top = shouldOpenUp
      ? Math.max(viewportPadding, rect.top - gap)
      : rect.bottom + gap;
    let left = rect.right - dropdownWidth;

    if (left < viewportPadding) left = viewportPadding;
    if (left + dropdownWidth > window.innerWidth - viewportPadding) {
      left = Math.max(viewportPadding, window.innerWidth - dropdownWidth - viewportPadding);
    }

    setDropdownStyle({
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      transform: shouldOpenUp ? 'translateY(-100%)' : 'none',
      transformOrigin: shouldOpenUp ? 'bottom right' : 'top right',
      maxHeight: `${maxHeight}px`,
      overflowY: 'auto'
    });
  }, []);
  
  useEffect(() => {
    if (!isOpen) return;
    updateDropdownPosition();

    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);
    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [isOpen, updateDropdownPosition]);

  const getVariantClass = (variant?: string) => {
    switch (variant) {
      case 'danger': return 'action-menu-item-danger';
      case 'warning': return 'action-menu-item-warning';
      case 'success': return 'action-menu-item-success';
      case 'info': return 'action-menu-item-info';
      case 'primary': return 'action-menu-item-primary';
      default: return '';
    }
  };

  return (
    <div className="action-menu-wrapper" ref={menuRef}>
      <button
        ref={buttonRef}
        className={`btn ${triggerSize === 'sm' ? 'btn-sm ' : ''}btn-link action-menu-trigger`}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        aria-label="Ações"
      >
        <svg
          className="action-menu-trigger-icon"
          width={triggerSize === 'sm' ? 14 : 18}
          height={triggerSize === 'sm' ? 14 : 18}
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
          focusable="false"
        >
          <circle cx="8" cy="3" r="1.5" />
          <circle cx="8" cy="8" r="1.5" />
          <circle cx="8" cy="13" r="1.5" />
        </svg>
      </button>
      {isOpen && createPortal(
        <div ref={dropdownRef} className="action-menu-dropdown" style={dropdownStyle}>
          {actions.map((action) => (
            <button
              key={action.key}
              className={`action-menu-item ${getVariantClass(action.variant)}`}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
                setIsOpen(false);
              }}
              disabled={action.disabled}
            >
              {action.icon && <span className="action-menu-icon">{action.icon}</span>}
              <span className="action-menu-label">{action.label}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

// Componente de menu de opções da coluna (ordenação e filtro)
const ColumnOptionsMenu: React.FC<{
  column: DataTableColumn;
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc';
  onSort?: (column: string, direction?: 'asc' | 'desc') => void;
  filterValue?: string;
  onFilter?: (columnKey: string, value: string) => void;
  onOpenColumnModal?: () => void;
  onOpenExportModal?: () => void;
}> = ({ column, sortColumn, sortDirection, onSort, filterValue, onFilter, onOpenColumnModal, onOpenExportModal }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localFilter, setLocalFilter] = useState(filterValue || '');
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Node;
    const clickedTrigger = !!menuRef.current && menuRef.current.contains(target);
    const clickedDropdown = !!dropdownRef.current && dropdownRef.current.contains(target);
    if (!clickedTrigger && !clickedDropdown) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, handleClickOutside]);

  useEffect(() => {
    setLocalFilter(filterValue || '');
  }, [filterValue]);

  const handleFilterChange = (value: string) => {
    setLocalFilter(value);
    if (onFilter) {
      onFilter(column.key, value);
    }
  };

  const handleClearFilter = () => {
    setLocalFilter('');
    if (onFilter) {
      onFilter(column.key, '');
    }
  };

  const isSorted = sortColumn === column.key;

  // Calcular posição do dropdown
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const updateDropdownPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const dropdownWidth = 260;
    const viewportPadding = 8;
    const gap = 4;
    const minPreferredHeight = 160;
    const availableBelow = window.innerHeight - (buttonRect.bottom + gap) - viewportPadding;
    const availableAbove = buttonRect.top - gap - viewportPadding;
    const shouldOpenUp = availableBelow < minPreferredHeight && availableAbove > availableBelow;
    const availableSpace = shouldOpenUp ? availableAbove : availableBelow;
    const maxHeight = Math.max(140, Math.min(window.innerHeight - viewportPadding * 2, availableSpace));

    const top = shouldOpenUp
      ? Math.max(viewportPadding, buttonRect.top - gap)
      : buttonRect.bottom + gap;
    let left = buttonRect.left;

    if (left < viewportPadding) left = viewportPadding;
    if (left + dropdownWidth > window.innerWidth - viewportPadding) {
      left = Math.max(viewportPadding, window.innerWidth - dropdownWidth - viewportPadding);
    }

    setDropdownStyle({
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      transform: shouldOpenUp ? 'translateY(-100%)' : 'none',
      transformOrigin: shouldOpenUp ? 'bottom left' : 'top left',
      maxHeight: `${maxHeight}px`,
      overflowY: 'auto'
    });
  }, []);
  
  useEffect(() => {
    if (!isOpen) return;
    updateDropdownPosition();

    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);
    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [isOpen, updateDropdownPosition]);

  return (
    <div className="data-table-column-options" ref={menuRef}>
      <button
        ref={buttonRef}
        className="data-table-column-options-trigger"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        aria-label="Opções da coluna"
      >
        ⋯
      </button>
      {isOpen && createPortal(
        <div ref={dropdownRef} className="data-table-column-options-dropdown" style={dropdownStyle}>
          {column.sortable !== false && onSort && (
            <div className="data-table-column-options-section">
              <div className="data-table-column-options-label">Ordenação</div>
              <button
                className={`data-table-column-option-item ${isSorted && sortDirection === 'asc' ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onSort(column.key, 'asc');
                  setIsOpen(false);
                }}
              >
                <span className="data-table-column-option-icon">▲</span>
                <span>Ordenar A-Z</span>
              </button>
              <button
                className={`data-table-column-option-item ${isSorted && sortDirection === 'desc' ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onSort(column.key, 'desc');
                  setIsOpen(false);
                }}
              >
                <span className="data-table-column-option-icon">▼</span>
                <span>Ordenar Z-A</span>
              </button>
            </div>
          )}
          {onFilter && (
            <div className="data-table-column-options-section">
              <div className="data-table-column-options-label">Filtro</div>
              <div className="data-table-filter-input">
                <input
                  type="text"
                  placeholder={`Filtrar ${column.label}...`}
                  value={localFilter}
                  onChange={(e) => handleFilterChange(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              {localFilter && (
                <button
                  className="data-table-column-option-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearFilter();
                  }}
                >
                  <span className="data-table-column-option-icon">✕</span>
                  <span>Limpar filtro</span>
                </button>
              )}
            </div>
          )}
          {onOpenColumnModal && (
            <div className="data-table-column-options-section">
              <button
                className="data-table-column-option-item"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  onOpenColumnModal();
                }}
              >
                <span className="data-table-column-option-icon">☰</span>
                <span>Selecionar colunas</span>
              </button>
            </div>
          )}
          {onOpenExportModal && (
            <div className="data-table-column-options-section">
              <button
                className="data-table-column-option-item"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  onOpenExportModal();
                }}
              >
                <span className="data-table-column-option-icon">⤓</span>
                <span>Exportar CSV</span>
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

function DataTable<T = any>({
  columns,
  data,
  keyExtractor,
  sortColumn,
  sortDirection = 'asc',
  onSort,
  onClearSort,
  onRowClick,
  loading = false,
  emptyMessage = 'Nenhum registro encontrado',
  striped = true,
  hover = true,
  bordered = false,
  responsive = true,
  size = 'md',
  className = '',
  showColumnMenu = true,
  onFilter,
  onClearFilters,
  filters,
  pagination = false,
  clientSidePagination = false,
  currentPage = 1,
  pageSize = 10,
  totalItems = 0,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [5, 10, 25, 50],
  initialVisibleColumns = {},
  exportFileName = 'export',
}: DataTableProps<T>) {
  // Estados para redimensionamento de colunas
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>(() => {
    const initialWidths: { [key: string]: number } = {};
    columns.forEach(col => {
      if (col.width) {
        initialWidths[col.key] = col.width;
      }
    });
    return initialWidths;
  });

  // Estado para visibilidade de colunas
  const [visibleColumns, setVisibleColumns] = useState<{ [key: string]: boolean }>(() => {
    const initial: { [key: string]: boolean } = {};
    columns.forEach(col => {
      initial[col.key] = initialVisibleColumns[col.key] ?? true;
    });
    return initial;
  });

  // Estado para redimensionamento ativo
  const [resizing, setResizing] = useState<{ key: string; startX: number; startWidth: number } | null>(null);
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportColumnOrder, setExportColumnOrder] = useState<string[]>([]);
  const [exportSelectedColumns, setExportSelectedColumns] = useState<{ [key: string]: boolean }>({});
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [internalSortColumn, setInternalSortColumn] = useState<string | null>(sortColumn || null);
  const [internalSortDirection, setInternalSortDirection] = useState<'asc' | 'desc'>(sortDirection);
  const [internalFilters, setInternalFilters] = useState<{ [key: string]: string }>(() => filters || {});

  useEffect(() => {
    setInternalSortColumn(sortColumn || null);
  }, [sortColumn]);

  useEffect(() => {
    setInternalSortDirection(sortDirection);
  }, [sortDirection]);

  useEffect(() => {
    if (typeof filters === 'undefined') return;
    setInternalFilters(filters || {});
  }, [filters]);

  // Handle para iniciar redimensionamento
  const handleResizeStart = (e: React.MouseEvent, columnKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    const currentWidth = columnWidths[columnKey] || 150;
    setResizing({ key: columnKey, startX: e.clientX, startWidth: currentWidth });
  };

  // Handle para movimentação do mouse durante redimensionamento
  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!resizing) return;
    
    const column = columns.find(col => col.key === resizing.key);
    const minWidth = column?.minWidth || 50;
    const diff = e.clientX - resizing.startX;
    const newWidth = Math.max(minWidth, resizing.startWidth + diff);
    
    setColumnWidths(prev => ({
      ...prev,
      [resizing.key]: newWidth
    }));
  }, [resizing, columns]);

  // Handle para finalizar redimensionamento
  const handleResizeEnd = useCallback(() => {
    setResizing(null);
  }, []);

  // Adicionar listeners de mouse para redimensionamento
  useEffect(() => {
    if (resizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [resizing, handleResizeMove, handleResizeEnd]);


  // Removido: ordenação só deve ocorrer pelo menu de opções

  const getCellValue = (row: T, column: DataTableColumn<T>, index: number) => {
    const value = (row as any)[column.key];
    if (column.render) {
      return column.render(value, row, index);
    }
    return value !== null && value !== undefined ? String(value) : '';
  };

  const effectiveSortColumn = onSort ? (sortColumn || null) : internalSortColumn;
  const effectiveSortDirection = onSort ? sortDirection : internalSortDirection;
  const effectiveFilters = onFilter ? (filters || {}) : internalFilters;

  const handleEffectiveSort = (column: string, direction?: 'asc' | 'desc') => {
    if (onSort) {
      onSort(column, direction);
      return;
    }
    if (direction) {
      setInternalSortColumn(column);
      setInternalSortDirection(direction);
      return;
    }
    if (internalSortColumn === column) {
      setInternalSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setInternalSortColumn(column);
      setInternalSortDirection('asc');
    }
  };

  const handleEffectiveFilter = (columnKey: string, value: string) => {
    if (onFilter) {
      onFilter(columnKey, value);
      return;
    }
    setInternalFilters((prev) => ({ ...prev, [columnKey]: value }));
  };

  const toggleColumnVisibility = (columnKey: string) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const filteredColumns = columns.filter(col => visibleColumns[col.key]);

  const activeFilters = Object.entries(effectiveFilters)
    .map(([key, value]) => ({ key, value: String(value || '').trim() }))
    .filter((item) => item.value.length > 0)
    .map((item) => {
      const column = columns.find((col) => col.key === item.key);
      return {
        key: item.key,
        label: column?.label || item.key,
        value: item.value,
      };
    });

  const hasActiveSort = Boolean(effectiveSortColumn);
  const showClearControls = activeFilters.length > 0 || hasActiveSort;

  const clearAllFilters = () => {
    const hadActiveFilters = activeFilters.length > 0;

    if (onClearFilters && hadActiveFilters) {
      onClearFilters();
    } else {
      activeFilters.forEach((item) => handleEffectiveFilter(item.key, ''));
    }

    if (onSort) {
      // When filters are controlled by parent, clearing sort in the same click can
      // trigger a second fetch using stale filter state and overwrite the unfiltered result.
      if (onClearFilters && hadActiveFilters) return;
      onClearSort?.();
      return;
    }

    setInternalSortColumn(null);
    setInternalSortDirection('asc');
  };

  const tableClasses = [
    'data-table',
    'table',
    striped && 'table-striped',
    hover && 'table-hover',
    bordered && 'table-bordered',
    size === 'sm' && 'table-sm',
    resizing && 'data-table-resizing',
    className,
  ].filter(Boolean).join(' ');

  const normalizeString = (value: any) => String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  const extractTextFromNode = (node: any): string => {
    if (node === null || node === undefined) return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (typeof node === 'boolean') return node ? 'Sim' : 'Não';
    if (Array.isArray(node)) return node.map(extractTextFromNode).join(' ');
    if (React.isValidElement(node)) {
      return extractTextFromNode((node as any).props?.children);
    }
    return '';
  };

  const resolveValue = (row: T, key: string) => {
    const raw = row as any;
    if (raw[key] !== undefined && raw[key] !== null) {
      return typeof raw[key] === 'boolean' ? (raw[key] ? 'Sim' : 'Não') : raw[key];
    }
    const aliases: { [k: string]: string[] } = {
      process: ['proc'],
      subprocess: ['sub'],
      activity: ['act'],
      userCount: ['usersCount'],
      client: ['clientId'],
    };
    const alias = aliases[key]?.find((a) => raw[a] !== undefined && raw[a] !== null);
    if (alias) {
      const aliasValue = raw[alias];
      return typeof aliasValue === 'boolean' ? (aliasValue ? 'Sim' : 'Não') : aliasValue;
    }
    const column = columns.find((col) => col.key === key);
    if (column?.render) {
      const rendered = column.render(raw[key], row, 0);
      const renderedText = extractTextFromNode(rendered).trim();
      if (renderedText) return renderedText;
    }
    return '';
  };

  const escapeCsvValue = (value: any): string => {
    const text = String(value ?? '');
    if (/[",\n\r;]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };

  useEffect(() => {
    const exportable = columns
      .filter((col) => !col.checkboxColumn)
      .map((col) => col.key);

    setExportColumnOrder((prev) => {
      const validPrev = prev.filter((key) => exportable.includes(key));
      const missing = exportable.filter((key) => !validPrev.includes(key));
      return [...validPrev, ...missing];
    });

    setExportSelectedColumns((prev) => {
      const next: { [key: string]: boolean } = {};
      exportable.forEach((key) => {
        const defaultSelected = key !== 'actions' && key !== 'acao' && key !== 'acoes';
        next[key] = prev[key] ?? defaultSelected;
      });
      return next;
    });
  }, [columns]);

  const moveExportColumn = (key: string, direction: 'up' | 'down') => {
    setExportColumnOrder((prev) => {
      const idx = prev.indexOf(key);
      if (idx < 0) return prev;
      const nextIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (nextIdx < 0 || nextIdx >= prev.length) return prev;
      const next = [...prev];
      const temp = next[idx];
      next[idx] = next[nextIdx];
      next[nextIdx] = temp;
      return next;
    });
  };

  const runCsvExport = async () => {
    const selectedColumns = exportColumnOrder.filter((key) => exportSelectedColumns[key]);
    if (!selectedColumns.length) return;

    const header = selectedColumns.map((key) => {
      const col = columns.find((c) => c.key === key);
      return escapeCsvValue(col?.label || key);
    }).join(';');

    const lines: string[] = [header];
    const chunkSize = 1000;
    const totalRows = processedData.length;

    setIsExporting(true);
    setExportProgress(0);

    try {
      for (let start = 0; start < totalRows; start += chunkSize) {
        const chunk = processedData.slice(start, start + chunkSize);
        chunk.forEach((row) => {
          const line = selectedColumns.map((key) => {
            const col = columns.find((c) => c.key === key);
            let value: any = (row as any)?.[key];
            if (col?.render) {
              const rendered = col.render(value, row, 0);
              const renderedText = extractTextFromNode(rendered).trim();
              if (renderedText) value = renderedText;
              else value = resolveValue(row, key);
            } else {
              value = resolveValue(row, key);
            }
            return escapeCsvValue(value);
          }).join(';');
          lines.push(line);
        });

        const done = Math.min(start + chunk.length, totalRows);
        const pct = totalRows === 0 ? 100 : Math.round((done / totalRows) * 100);
        setExportProgress(pct);
        await new Promise<void>((resolve) => setTimeout(resolve, 0));
      }

      const csvContent = '\ufeff' + lines.join('\r\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const dateSuffix = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
      a.href = url;
      a.download = `${exportFileName || 'export'}-${dateSuffix}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportModalOpen(false);
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  const processedData = (data || []).filter((row) => {
    return Object.entries(effectiveFilters || {}).every(([columnKey, filterValue]) => {
      const normalizedFilter = normalizeString(filterValue).trim();
      if (!normalizedFilter) return true;
      const value = resolveValue(row, columnKey);
      return normalizeString(value).includes(normalizedFilter);
    });
  });

  if (effectiveSortColumn) {
    const toSortableNumber = (value: any): number | null => {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value !== 'string') return null;
      const trimmed = value.trim();
      if (!trimmed) return null;
      if (!/^-?\d+(?:[.,]\d+)?$/.test(trimmed)) return null;
      const normalized = trimmed.includes(',') && !trimmed.includes('.')
        ? trimmed.replace(',', '.')
        : trimmed.replace(/,/g, '');
      const numeric = Number(normalized);
      return Number.isFinite(numeric) ? numeric : null;
    };

    processedData.sort((a, b) => {
      const rawA = resolveValue(a, effectiveSortColumn);
      const rawB = resolveValue(b, effectiveSortColumn);

      const numA = toSortableNumber(rawA);
      const numB = toSortableNumber(rawB);
      if (numA !== null && numB !== null) {
        if (numA < numB) return effectiveSortDirection === 'asc' ? -1 : 1;
        if (numA > numB) return effectiveSortDirection === 'asc' ? 1 : -1;
        return 0;
      }

      const textA = normalizeString(rawA);
      const textB = normalizeString(rawB);
      if (textA < textB) return effectiveSortDirection === 'asc' ? -1 : 1;
      if (textA > textB) return effectiveSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }

  const computedTotalItems = clientSidePagination ? processedData.length : totalItems;
  const totalPages = pagination && computedTotalItems > 0 ? Math.ceil(computedTotalItems / pageSize) : 1;
  const safePage = Math.min(Math.max(1, currentPage), Math.max(1, totalPages));
  const renderData = clientSidePagination
    ? processedData.slice((safePage - 1) * pageSize, safePage * pageSize)
    : processedData;
  
  // Gerar array de números de páginas para exibir
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5; // número máximo de páginas visíveis
    
    if (totalPages <= maxVisible) {
      // Se tem poucas páginas, mostra todas
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Sempre mostra primeira página
      pages.push(1);
      
      if (safePage > 3) {
        pages.push('...');
      }
      
      // Páginas ao redor da página atual
      const start = Math.max(2, safePage - 1);
      const end = Math.min(totalPages - 1, safePage + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (safePage < totalPages - 2) {
        pages.push('...');
      }
      
      // Sempre mostra última página
      pages.push(totalPages);
    }
    
    return pages;
  };

  const TableContent = (
    <>
      {showClearControls && (
        <div className="data-table-active-filters">
          {activeFilters.length > 0 ? (
            <div className="data-table-active-filters-list">
              {activeFilters.map((item) => (
                <span key={item.key} className="data-table-active-filter-pill">
                  <strong>{item.label}:</strong>&nbsp;{item.value}
                </span>
              ))}
            </div>
          ) : <div />}
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={clearAllFilters}
          >
            Limpar todos os filtros
          </button>
        </div>
      )}
      <table className={tableClasses}>
        <thead>
          <tr>
            {filteredColumns.map((column) => (
              <th
                key={column.key}
                className={[
                  column.className,
                ].filter(Boolean).join(' ')}
                style={{
                  width: columnWidths[column.key] ? `${columnWidths[column.key]}px` : column.width ? `${column.width}px` : undefined,
                  minWidth: column.minWidth ? `${column.minWidth}px` : undefined,
                  position: 'relative',
                  userSelect: 'none'
                }}
              >
                <div className="data-table-header-content">
                  <div className="data-table-header-left">
                    {column.checkboxColumn ? (
                      <input
                        type="checkbox"
                        checked={column.allChecked || false}
                        onChange={(e) => {
                          e.stopPropagation();
                          column.onCheckboxHeaderClick?.();
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                    ) : (
                      <span>{column.label}</span>
                    )}
                  </div>
                  {!column.checkboxColumn && (
                    <ColumnOptionsMenu
                      column={column}
                      sortColumn={effectiveSortColumn || null}
                      sortDirection={effectiveSortDirection || 'asc'}
                      onSort={handleEffectiveSort}
                      filterValue={effectiveFilters[column.key]}
                      onFilter={handleEffectiveFilter}
                      onOpenColumnModal={() => setColumnModalOpen(true)}
                      onOpenExportModal={() => setExportModalOpen(true)}
                    />
                  )}
                </div>
                {column.resizable !== false && (
                  <div
                    className="data-table-resize-handle"
                    onMouseDown={(e) => handleResizeStart(e, column.key)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={filteredColumns.length} className="text-center py-4">
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Carregando...</span>
                </div>
                <span>Carregando...</span>
              </td>
            </tr>
          ) : renderData.length === 0 ? (
            <tr>
              <td colSpan={filteredColumns.length} className="text-center text-muted py-4">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            renderData.map((row, index) => (
              <tr
                key={keyExtractor(row, index)}
                onClick={onRowClick ? () => onRowClick(row, index) : undefined}
                style={onRowClick ? { cursor: 'pointer' } : undefined}
              >
                {filteredColumns.map((column) => (
                  <td
                    key={column.key}
                    data-label={column.label}
                    className={column.className}
                  >
                    {getCellValue(row, column, index)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
      {pagination && computedTotalItems > 0 && (
        <div className="data-table-pagination">
          <div className="data-table-pagination-info">
            <span className="data-table-pagination-label">Itens por página:</span>
            <select
              className="data-table-pagination-select"
              value={pageSize}
              onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <span className="data-table-pagination-summary">
              {Math.min((safePage - 1) * pageSize + 1, computedTotalItems)}-{Math.min(safePage * pageSize, computedTotalItems)} de {computedTotalItems}
            </span>
          </div>
          <div className="data-table-pagination-controls">
            <button
              className="data-table-pagination-button"
              onClick={() => onPageChange?.(1)}
              disabled={safePage === 1}
              title="Primeira página"
            >
              «
            </button>
            <button
              className="data-table-pagination-button"
              onClick={() => onPageChange?.(safePage - 1)}
              disabled={safePage === 1}
              title="Página anterior"
            >
              ‹
            </button>
            {getPageNumbers().map((page, idx) => (
              typeof page === 'number' ? (
                <button
                  key={idx}
                  className={`data-table-pagination-button ${safePage === page ? 'active' : ''}`}
                  onClick={() => onPageChange?.(page)}
                >
                  {page}
                </button>
              ) : (
                <span key={idx} className="data-table-pagination-ellipsis">{page}</span>
              )
            ))}
            <button
              className="data-table-pagination-button"
              onClick={() => onPageChange?.(safePage + 1)}
              disabled={safePage === totalPages}
              title="Próxima página"
            >
              ›
            </button>
            <button
              className="data-table-pagination-button"
              onClick={() => onPageChange?.(totalPages)}
              disabled={safePage === totalPages}
              title="Última página"
            >
              »
            </button>
          </div>
        </div>
      )}
      {columnModalOpen && (
        <div className="data-table-modal-overlay" onClick={() => setColumnModalOpen(false)}>
          <div className="data-table-modal" onClick={(e) => e.stopPropagation()}>
            <div className="data-table-modal-header">
              <h5>Selecionar Colunas</h5>
              <button 
                className="data-table-modal-close"
                onClick={() => setColumnModalOpen(false)}
                aria-label="Fechar"
              >
                ✕
              </button>
            </div>
            <div className="data-table-modal-body">
              {columns.map(col => (
                <label key={col.key} className="data-table-modal-checkbox-item">
                  <input
                    type="checkbox"
                    checked={visibleColumns[col.key]}
                    onChange={() => toggleColumnVisibility(col.key)}
                  />
                  <span>{col.label}</span>
                </label>
              ))}
            </div>
            <div className="data-table-modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setColumnModalOpen(false)}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
      {exportModalOpen && (
        <div className="data-table-modal-overlay" onClick={() => !isExporting && setExportModalOpen(false)}>
          <div className="data-table-modal" onClick={(e) => e.stopPropagation()}>
            <div className="data-table-modal-header">
              <h5>Exportar CSV</h5>
              <button
                className="data-table-modal-close"
                onClick={() => !isExporting && setExportModalOpen(false)}
                aria-label="Fechar"
                disabled={isExporting}
              >
                ✕
              </button>
            </div>
            <div className="data-table-modal-body">
              <div className="mb-2">
                <small className="text-muted">Selecione as colunas e ajuste a ordem de exportação. A exportação inclui 100% dos dados da tabela (não apenas a página atual).</small>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    const next: { [key: string]: boolean } = {};
                    exportColumnOrder.forEach((key) => { next[key] = true; });
                    setExportSelectedColumns(next);
                  }}
                  disabled={isExporting}
                >
                  Selecionar todas
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    const next: { [key: string]: boolean } = {};
                    exportColumnOrder.forEach((key) => { next[key] = false; });
                    setExportSelectedColumns(next);
                  }}
                  disabled={isExporting}
                >
                  Limpar seleção
                </button>
              </div>

              {exportColumnOrder.map((key, idx) => {
                const col = columns.find((c) => c.key === key);
                if (!col) return null;
                return (
                  <div key={key} className="data-table-modal-checkbox-item" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={!!exportSelectedColumns[key]}
                      onChange={() => setExportSelectedColumns((prev) => ({ ...prev, [key]: !prev[key] }))}
                      disabled={isExporting}
                    />
                    <span style={{ flex: 1 }}>{col.label}</span>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => moveExportColumn(key, 'up')}
                        disabled={isExporting || idx === 0}
                        title="Mover para cima"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => moveExportColumn(key, 'down')}
                        disabled={isExporting || idx === exportColumnOrder.length - 1}
                        title="Mover para baixo"
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                );
              })}

              {isExporting && (
                <div className="mt-3">
                  <div className="progress" style={{ height: 8 }}>
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{ width: `${exportProgress}%` }}
                      aria-valuenow={exportProgress}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    ></div>
                  </div>
                  <small className="text-muted">Exportando... {exportProgress}%</small>
                </div>
              )}
            </div>
            <div className="data-table-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setExportModalOpen(false)}
                disabled={isExporting}
              >
                Fechar
              </button>
              <button
                className="btn btn-primary"
                onClick={runCsvExport}
                disabled={isExporting || exportColumnOrder.filter((key) => exportSelectedColumns[key]).length === 0}
              >
                {isExporting ? 'Exportando...' : 'Exportar CSV'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return responsive ? (
    <div className="data-table-responsive">
      {TableContent}
    </div>
  ) : (
    TableContent
  );
}

export default DataTable;
